import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import React, {
  CSSProperties,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styled, { useTheme } from "styled-components";
import LoadingSpinnerOverlay, {
  LoadingSpinnerOverlayProps,
} from "common/Overlays/LoadingSpinnerOverlay";
import RefreshSpinner from "common/Lists/RefreshSpinner";
import useScrollView from "common/Scroll/useScrollView";
import {
  ACTION_BUTTON_WITH_MARGIN_HEIGHT,
  BOTTOM_NAVIGATOR,
} from "common/constants";
import { Range } from "@tanstack/virtual-core";

interface FlatListProps<T> {
  data: Array<T>;
  loading?: boolean;
  horizontal?: boolean;
  renderTopContainer?: () => ReactNode;
  renderContent: ({ item, index }: { item: T; index: number }) => any;
  itemHeight?: number;
  gap?: number;
  listStyle?: CSSProperties;
  onRefresh?: () => void;
  refreshing?: boolean;
  emptyState?: ReactNode;
  hasActionButton?: boolean;
  wrapperStyle?: CSSProperties;
  loadingProps?: LoadingSpinnerOverlayProps;
}

const FlatList = <T extends any>({
  data,
  loading,
  horizontal,
  renderTopContainer,
  renderContent,
  itemHeight,
  gap = 16,
  listStyle,
  onRefresh,
  refreshing,
  emptyState,
  hasActionButton,
  wrapperStyle,
  loadingProps,
}: FlatListProps<T>) => {
  const theme = useTheme();
  const { scrollRef, scrollViewProps, spinnerProps, showSpinner } =
    useScrollView({
      refreshing,
      loading,
      onRefresh,
    });
  const numToShowRef = useRef<number>(0);

  const rangeExtractor = useCallback(
    (range: Range) => {
      const numItems = range.endIndex - range.startIndex;
      const numToShow = Math.max(numToShowRef.current, numItems);
      if (data.length > 1) {
        numToShowRef.current = numToShow;
      }
      range.endIndex = range.startIndex + numToShow;
      return defaultRangeExtractor(range);
    },
    [data.length > 1]
  );

  const virtualizer = useVirtualizer({
    horizontal,
    count: data.length + +!!renderTopContainer,
    getScrollElement: useCallback(() => scrollRef.current, []),
    estimateSize: useCallback(() => 90, []),
    overscan: 1,
    rangeExtractor,
    useAnimationFrameWithResizeObserver: true,
  });

  virtualizer.shouldAdjustScrollPositionOnItemSizeChange = useCallback(
    () => false,
    []
  );
  const listHeight =
    horizontal && itemHeight
      ? itemHeight + PADDING_VERTICAL * 2 + SHADOW_HEIGHT
      : "100%";

  const items = virtualizer.getVirtualItems();

  const getVerticalList = () => {
    return (
      <VerticalListItems
        style={{
          // transform: `translateY(${items[0]?.start ?? 0}px)`,
          paddingBottom: hasActionButton ? ACTION_BUTTON_WITH_MARGIN_HEIGHT : 0,
          // willChange: "transform",
          ...listStyle,
        }}
      >
        {items.map((virtualItem) => {
          if (!!renderTopContainer && virtualItem.index === 0) {
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  transform: `translateY(${virtualItem.start}px)`,
                  width: "100%",
                  willChange: "transform",
                }}
              >
                {renderTopContainer?.()}
              </div>
            );
          }
          const itemIndex = virtualItem.index - +!!renderTopContainer;
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                transform: `translateY(${virtualItem.start}px)`,
                width: "100%",
              }}
            >
              <ContentWrapper $gap={gap} $isFirst={itemIndex === 0}>
                {renderContent({
                  item: data[itemIndex],
                  index: itemIndex,
                })}
              </ContentWrapper>
            </div>
          );
        })}
        {!data.length && emptyState}
      </VerticalListItems>
    );
  };
  const verticalList = useMemo(() => getVerticalList(), [items]);

  const getHorizontalList = () => {
    return items.map((virtualItem) => {
      return (
        <HorizontalItemWrapper
          key={virtualItem.key}
          data-index={virtualItem.index}
          ref={virtualizer.measureElement}
          style={{
            transform: `translateX(${virtualItem.start}px)`,
            willChange: "transform",
          }}
        >
          <ContentWrapper
            $gap={gap}
            $horizontal={true}
            $isFirst={virtualItem.index === 0}
          >
            {renderContent({
              item: data[virtualItem.index],
              index: virtualItem.index,
            })}
          </ContentWrapper>
        </HorizontalItemWrapper>
      );
    });
  };

  const style = useMemo(() => {
    return { height: listHeight };
  }, [listHeight]);

  return (
    <Wrapper
      ref={scrollRef}
      $horizontal={horizontal}
      style={style}
      {...scrollViewProps}
    >
      {showSpinner && <RefreshSpinner {...spinnerProps} />}
      <LoadingSpinnerOverlay
        isLoading={loading}
        spinnerSize="large"
        hideContentWhileLoading={loading}
        wrap={true}
        backgroundColor={theme.colors.layoutBgColor}
        {...loadingProps}
      >
        <InnerWrapper $horizontal={horizontal} style={wrapperStyle}>
          <ListWrapper
            $horizontal={horizontal}
            $totalSize={virtualizer.getTotalSize()}
          >
            {horizontal ? getHorizontalList() : verticalList}
          </ListWrapper>
        </InnerWrapper>
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default FlatList;

const PADDING_VERTICAL = 8;
const PADDING_HORIZONTAL = 16;
const SHADOW_HEIGHT = 16;

const Wrapper = styled.div<{ $horizontal?: boolean }>`
  width: 100%;
  position: relative;
  overflow: auto;
  @media (max-width: ${BOTTOM_NAVIGATOR}) {
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;
const InnerWrapper = styled.div<{ $horizontal?: boolean }>`
  height: 100%;
  padding: ${PADDING_VERTICAL}px ${PADDING_HORIZONTAL}px;
`;
const ListWrapper = styled.div.attrs((p) => ({
  style: {
    width: p.$horizontal ? `${p.$totalSize + PADDING_HORIZONTAL}px` : "100%",
    height: p.$horizontal ? "100%" : `${p.$totalSize}px`,
  },
}))<{
  $horizontal: boolean;
  $totalSize: number;
}>`
  position: relative;
`;
const VerticalListItems = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;
const HorizontalItemWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
`;
const ContentWrapper = styled.div<{
  $gap: number;
  $horizontal?: boolean;
  $isFirst?: boolean;
}>`
  padding: ${(p) =>
    p.$horizontal ? `0 ${p.$gap / 2}px` : `${p.$gap / 2}px 0`};
`;
